import React from 'react';
import { CheckIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import FundingBand from '../BiSections/FundingBand';

// Body of a BI platform page (below the hero), built from the platform
// page design mockups. Driven entirely by `useCase.components.platformPage`
// (see configs/platformPages/domo.js); every section is optional, so the
// other eight platforms can reuse this as their content is written.
// Registered as a `richContent` component in VideoReportStarterLayout.

// Step tag pill colors (matches Home's workflow tags).
const tagClasses = {
  'AI-led': 'bg-[#F3E8FF] text-[#6B21A8]',
};
const defaultTagClass = 'bg-primary-50 text-primary-600';

const Eyebrow = ({ children }) => (
  <div className="text-xs font-semibold uppercase tracking-wider text-primary-600">
    {children}
  </div>
);

const SectionHeader = ({ eyebrow, title, intro }) => (
  <div className="flex max-w-3xl flex-col gap-1.5">
    {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
    {title && <h2 className="text-2xl font-bold text-gray-900 sm:text-[28px]">{title}</h2>}
    {intro && <p className="text-[15px] leading-relaxed text-gray-600">{intro}</p>}
  </div>
);

const WhyMove = ({ eyebrow, title, intro, cards = [] }) => (
  <section className="flex flex-col gap-6">
    <SectionHeader eyebrow={eyebrow} title={title} intro={intro} />
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {cards.map((card) => (
        <article
          key={card.title}
          className="flex flex-col gap-2 rounded-lg bg-white p-5 ring-1 ring-inset ring-gray-200"
        >
          <h3 className="text-base font-semibold text-gray-900">{card.title}</h3>
          <p className="text-sm leading-relaxed text-gray-600">{card.description}</p>
        </article>
      ))}
    </div>
  </section>
);

const Metadata = ({ id, eyebrow, title, intro, columns = [], rows = [], benefits }) => (
  // scroll-mt leaves room above the heading when the hero button jumps here.
  <section id={id} className="flex scroll-mt-24 flex-col gap-6">
    <SectionHeader eyebrow={eyebrow} title={title} intro={intro} />
    <div className="flex flex-col gap-6 xl:flex-row xl:items-start">
      {rows.length > 0 && (
        <div className="min-w-0 overflow-x-auto rounded-lg bg-white ring-1 ring-inset ring-gray-200 xl:flex-[2]">
          <table className="w-full min-w-[640px] border-collapse text-left text-sm leading-normal">
            <thead>
              <tr className="bg-gray-50">
                {columns.map((column) => (
                  <th
                    key={column}
                    scope="col"
                    className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-600"
                  >
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.what} className="border-t border-gray-200">
                  <td className="px-4 py-3 font-semibold text-gray-900">{row.what}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-gray-700">{row.from}</td>
                  <td className="px-4 py-3 text-gray-700">{row.use}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {benefits && (
        <div className="flex flex-col gap-4 rounded-xl bg-[#EEF2FF] p-6 ring-1 ring-inset ring-[#C7D2FE] xl:flex-1">
          {benefits.eyebrow && <Eyebrow>{benefits.eyebrow}</Eyebrow>}
          {(benefits.items || []).map((item) => (
            <div key={item.title} className="flex gap-3">
              <CheckIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary-600" aria-hidden="true" />
              <div className="flex flex-col gap-0.5">
                <div className="text-[15px] font-semibold text-gray-900">{item.title}</div>
                <div className="text-sm leading-normal text-gray-600">{item.description}</div>
              </div>
            </div>
          ))}
          {benefits.note && (
            <div className="flex gap-2.5 border-t border-[#C7D2FE] pt-3.5 text-[13px] leading-normal text-gray-700">
              <LockClosedIcon className="mt-px h-[18px] w-[18px] flex-shrink-0 text-primary-600" aria-hidden="true" />
              <span>
                <span className="font-semibold text-gray-900">{benefits.note.lead}</span>{' '}
                {benefits.note.text}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  </section>
);

const Process = ({ eyebrow, title, intro, steps = [] }) => (
  <section className="flex flex-col gap-6">
    <SectionHeader eyebrow={eyebrow} title={title} intro={intro} />
    <ol className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
      {steps.map((step, index) => (
        <li
          key={step.title}
          className="flex flex-col gap-2.5 rounded-lg bg-white p-5 ring-1 ring-inset ring-gray-200"
        >
          <div className="flex items-center gap-2.5" aria-hidden="true">
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary-600 text-sm font-bold text-white">
              {index + 1}
            </div>
            <div className="h-0.5 flex-grow bg-[#C7D2FE]" />
          </div>
          {step.tag && (
            <span
              className={`inline-flex self-start rounded-full px-2.5 py-0.5 text-xs font-semibold ${tagClasses[step.tag] || defaultTagClass}`}
            >
              {step.tag}
            </span>
          )}
          <h3 className="text-base font-semibold leading-snug text-gray-900">
            <span className="sr-only">Step {index + 1}: </span>
            {step.title}
          </h3>
          <p className="text-sm leading-relaxed text-gray-600">{step.description}</p>
        </li>
      ))}
    </ol>
  </section>
);

const AfterMigration = ({ eyebrow, title, items = [] }) => (
  <section className="grid grid-cols-1 gap-6 rounded-xl bg-[#EEF2FF] p-7 ring-1 ring-inset ring-[#C7D2FE] md:grid-cols-2 lg:grid-cols-4">
    <div className="flex flex-col gap-2">
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      {title && <h3 className="text-xl font-bold leading-snug text-gray-900">{title}</h3>}
    </div>
    {items.map((item) => (
      <div key={item.title} className="flex flex-col gap-1.5">
        <div className="text-[15px] font-semibold text-gray-900">{item.title}</div>
        <div className="text-sm leading-normal text-gray-600">{item.description}</div>
      </div>
    ))}
  </section>
);

const PlatformMigrationContent = ({ useCase }) => {
  const page = useCase?.components?.platformPage;
  if (!page) {
    return null;
  }

  return (
    <div className="flex flex-col gap-14 pt-6">
      {page.whyMove && <WhyMove {...page.whyMove} />}
      {page.metadata && <Metadata {...page.metadata} />}
      {page.process && <Process {...page.process} />}
      {page.afterMigration && <AfterMigration {...page.afterMigration} />}
      {page.showFunding && <FundingBand className="" />}
    </div>
  );
};

export default PlatformMigrationContent;
